import React, { useState, useEffect, useContext } from "react";
import { Tooltip, Image, Avatar } from "antd";
import { AppstoreOutlined } from "@ant-design/icons";

import { DateFormat, getApi, saveApi, successMessage, errorMessage } from "../../../helpers";
import CardContainer from "../../../components/Card";
import TableContainer from "../../../components/Table";
import { StoreContext } from "../../../context";
import CategoryPopup from "../Category/Popup";
import SubCategorySidebar from "./Sidebar";
import { canWrite } from "../../../helpers/permissions";

const subCategoryColumns = [
  {
    title: "Name",
    dataIndex: "sub_category_name",
    key: "sub_category_name",
    sorter: true,
  },
  {
    title: "Category",
    dataIndex: "category_name",
    key: "category_name",
  },
  // {
  //   title: "Description",
  //   dataIndex: "description",
  //   key: "description",
  //   ellipsis: {
  //     showTitle: true,
  //   },
  // },
  {
    title: "Date",
    dataIndex: "created_at",
    key: "created_at",
    align: "center",
    render: (text) => DateFormat(text),
    sorter: true,
  },
  {
    title: "Image",
    dataIndex: "sub_category_image",
    key: "sub_category_image",
    align: "center",
    width: 100,
    // render: (text) => <Image width={50} preview={false} alt='' src={text} />,
    render: (text) => (
      <Avatar
        shape="square"
        icon={<AppstoreOutlined />}
        size={50}
        alt=""
        src={text}
      />
    ),
  },
];

export default function SubCategory() {
  const { storeId } = useContext(StoreContext);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(8);
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState({});
  const [sidebar, setSidebar] = useState(false);
  const [popup, setPopup] = useState(false);
  const [popupTitle, setPopupTitle] = useState("");
  const [savedPayload, setSavedPayload] = useState({});
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [searchedCategory, setSearchedCategory] = useState(false);

  useEffect(() => {
    getList();
    // Also fetch categories when component mounts
    getCategoryOptions();
  }, []);

  const getList = async (payload = {}) => {
    setLoading(true);
    const res = await getApi(payload, "getSubCategory");
    if (res.data) {
      setList(res.data);
      setTotal(res.meta?.total);
    }
    setSavedPayload(payload);
    setLoading(false);
  };

  const getCategoryOptions = async (keyword = '') => {
    const res = await getApi(
      { size: 100, keyword: keyword, type: "admin" },
      "getCategory"
    );
    if (res.data) {
      setCategoryOptions(res.data);
      setSearchedCategory(true);
    }
  };

  const saveHandler = async (values) => {
    setLoading(true);
    setPopup(false);
    setSidebar(false);
    
    try {
      // Step 1: Extract the image file from values
      const imageFile = values.file;
      delete values.file; // Remove file to avoid sending in JSON request
      
      // Step 2: Prepare the subcategory data
      let payload = { 
        name: values.name,
        description: values.description || "",
        is_active: values.is_active ? 1 : 0,
      };
      
      let apiName, msg;
      
      // For existing subcategory update
      if (selected.sub_category_id) {
        apiName = "updateSubCategory";
        msg = "Updated Sub Category";
        payload.sub_category_id = selected.sub_category_id;
      } 
      // For new subcategory creation
      else {
        apiName = "createSubCategory";
        msg = "Added Sub Category";
        payload.category_id = values.category_id;
        payload.getId = true; // Flag to get back the sub_category_id
      }
      
      // Step 3: Call API to create/update subcategory
      const response = await saveApi(payload, msg, apiName);
      
      // Step 4: If successful and we have an image, upload it separately
      if (response && response.success === true && imageFile) {
        // Get subcategory ID from response
        const subCategoryId = response.data.sub_category_id;
        console.log("SubCategory created with ID:", subCategoryId);
        
        // Create FormData for image upload
        const formData = new FormData();
        formData.append("sub_category_id", subCategoryId);
        formData.append("sub_category_img", imageFile);
        
        // Upload the image
        const headers = { "content-type": "multipart/form-data" };
        const imageResponse = await getApi(formData, "uploadSubCategoryImage", "ecomm/", headers);
        
        if (imageResponse.data) {
          successMessage("Sub Category Image Uploaded");
        } else {
          errorMessage("Failed to upload image");
        }
      }
      
      // Handle subcategory groups if any
      if (response && response.success && values.sub_category_groups?.length) {
        const subCategoryId = response.data.sub_category_id;
        
        await Promise.all(values.sub_category_groups.map(async (group) => {
          if (!group.name) return; // Skip empty groups
          
          // Step 1: Create the group
          const groupPayload = {
            name: group.name,
            description: group.description || "",
            sub_category_id: subCategoryId,
            getId: true // Flag to get back the group ID
          };
          
          const groupResponse = await saveApi(
            groupPayload,
            "",
            "createSubCategoryGroup"
          );
          
          // Step 2: If group created successfully and has image, upload it separately
          if (groupResponse && groupResponse.success && groupResponse.data.sub_category_group_id && group.file) {
            console.log("SubCategory Group created with ID:", groupResponse.data.sub_category_group_id);
            
            // Create FormData for group image upload
            const formData = new FormData();
            const headers = { "content-type": "multipart/form-data" };
            formData.append("sub_category_group_id", groupResponse.data.sub_category_group_id);
            formData.append("sub_category_group_img", group.file);
            
            // Upload the group image
            const imageResponse = await getApi(formData, "uploadSubCategoryGroupImage", "ecomm/", headers);
            
            if (imageResponse.data) {
              console.log("Group image uploaded successfully");
            } else {
              console.error("Failed to upload group image");
            }
          }
        }));
      }
      
      // Refresh the list
      getList(savedPayload);
    } catch (error) {
      console.error("Error in subcategory save process:", error);
      errorMessage("Failed to save subcategory");
      setLoading(false);
    }
  };

  return (
    <>
      {popup && (
        <CategoryPopup
          subCategory={true}
          isSubCategoryGroup={selected.sub_category_group_id ? true : false}
          title={popupTitle}
          data={selected}
          visible={popup}
          categoryOptions={categoryOptions}
          uploadImage={selected.sub_category_id ? false : true}
          saveHandler={(values) => saveHandler(values)}
          cancelHandler={() => setPopup(false)}
        />
      )}
      {sidebar && (
        <SubCategorySidebar
          data={selected}
          visible={sidebar}
          editCategory={() => (
            setPopupTitle("Edit Sub Category Details"), setPopup(true)
          )}
          onUploadDone={() => (setSidebar(false), getList(savedPayload))}
          onClose={() => setSidebar(false)}
        />
      )}
      <CardContainer bordered={false} hoverable={false}>
        <TableContainer
          breadcrumbTitle="Sub Category Management"
          loading={loading}
          total={total}
          columns={subCategoryColumns}
          data={list}
          searchFor="Name, Category"
          addButton={canWrite() && (storeId ? false : true)}
          addHandler={() => {
            setSelected({});
            setPopupTitle("Add Sub Category");
            setPopup(true);
            getCategoryOptions();
          }}
          rowClickHandler={(record) => (
            setSelected(record), setSidebar(true)
          )}
          dropdownSearchHandler={(v) => getCategoryOptions(v)}
          dropdownFilterOptions={
            categoryOptions.length > 0
              ? categoryOptions.map(
                (category) =>
                (category = {
                  value: category.name,
                  key: category.category_id,
                })
              )
              : [
                {
                  label: searchedCategory
                    ? "No Such Category"
                    : "Type to Search Category",
                  disabled: true,
                },
              ]
          }
          functionsHandler={getList}
          scrollable={false}
        />
      </CardContainer>
    </>
  );
}
